import CheckIcon from "@mui/icons-material/Check";
import {
	Box,
	Card,
	CardContent,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	Typography
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import FreelancerOnboardingApplicationMessage from "../../components/freelancer/onboarding/FreelancerOnboardingApplicationMessage";
import FreelancerOnboardingExperienceRating from "../../components/freelancer/onboarding/FreelancerOnboardingExperienceRating";
import { FreelancerProfile } from "../../models/FreelancerProfile";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { zoullooColors } from "../../theme";
import { Link } from "react-router-dom";
import { LanguageCode } from "../../models/enums/LanguageCode";
import { useUserSettings } from "../../hooks/useUser";
import { useFreelancerSoftSkillsEvaluations } from "../../hooks/useFreelancerOnboarding";

interface CustomStepIconProps {
	active?: boolean;
	completed?: boolean;
	index: number;
}

const CustomStepIcon: React.FC<CustomStepIconProps> = ({ active, completed, index }) => {
	if (completed) {
		return (
			<Box
				sx={{
					width: 32,
					height: 32,
					borderRadius: "50%",
					backgroundColor: zoullooColors.success.main,
					color: "white",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: "1rem",
					fontWeight: "bold"
				}}
			>
				<CheckIcon
					sx={{
						stroke: zoullooColors.background.default
					}}
				/>
			</Box>
		);
	}

	if (active) {
		return (
			<Box
				sx={{
					width: 32,
					height: 32,
					borderRadius: "50%",
					backgroundColor: zoullooColors.secondary.main,
					color: "white",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: "1rem",
					fontWeight: "bold"
				}}
			>
				{index + 1}
			</Box>
		);
	}

	return (
		<Box
			sx={{
				width: 32,
				height: 32,
				borderRadius: "50%",
				backgroundColor: "#d1d5db",
				color: "#6b7280",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontSize: "1rem"
			}}
		>
			{index + 1}
		</Box>
	);
};

interface FreelancerApplicationProps {
	freelancerProfile: FreelancerProfile;
}

const FreelancerApplication: React.FC<FreelancerApplicationProps> = ({
	freelancerProfile
}) => {
	const { t } = useTranslation();
	const { data: userSettings } = useUserSettings(freelancerProfile.freelancer?.id ?? 0);
	const { data: softSkillsEvaluations } = useFreelancerSoftSkillsEvaluations(
		freelancerProfile.onboarding?.id ?? 0
	);
	const steps = [
		{
			label: t("freelancerApplication.steps.profileQualification"),
			description: "",
			completed:
				freelancerProfile.onboarding.currentStatus >
				FreelancerOnboardingStatus.NotSubmitted,
			active:
				freelancerProfile.onboarding.currentStatus ===
				FreelancerOnboardingStatus.NotSubmitted
		},
		{
			label: t("freelancerApplication.steps.talentAgentExchange"),
			description: (
				<Link
					to={
						userSettings?.preferredLanguage === LanguageCode.Fr
							? "https://calendar.app.google/hAzXjfuM1xm9eq6BA"
							: "https://calendar.app.google/TAYNi4HJ3pRcczMX7"
					}
					style={{
						textDecoration: "none",
						backgroundColor: zoullooColors.secondary.main,
						color: "white",
						padding: "10px",
						borderRadius: "8px",
						top: "9px",
						position: "relative"
					}}
					target="_blank"
				>
					{t("freelancerApplication.steps.talentAgentExchangeButton")}
				</Link>
			),
			completed:
				freelancerProfile.onboarding.currentStatus > FreelancerOnboardingStatus.Interview,
			active:
				freelancerProfile.onboarding.currentStatus ===
					FreelancerOnboardingStatus.Submitted ||
				freelancerProfile.onboarding.currentStatus ===
					FreelancerOnboardingStatus.Interview
		},
		{
			label: t("freelancerApplication.steps.softSkillsEvaluation"),
			description: softSkillsEvaluations?.[0]?.evaluationUrl && (
				<Link
					to={softSkillsEvaluations?.[0]?.evaluationUrl}
					style={{
						textDecoration: "none",
						backgroundColor: zoullooColors.secondary.main,
						color: "white",
						padding: "10px",
						borderRadius: "8px",
						top: "9px",
						position: "relative"
					}}
					target="_blank"
				>
					{t("freelancerApplication.steps.softSkillsEvaluationButton")}
				</Link>
			),
			completed:
				freelancerProfile.onboarding.currentStatus >
				FreelancerOnboardingStatus.SoftSkillsEvaluation,
			active:
				freelancerProfile.onboarding.currentStatus ===
				FreelancerOnboardingStatus.SoftSkillsEvaluation
		},
		{
			label: t("freelancerApplication.steps.hardSkillsEvaluation"),
			description: "",
			completed:
				freelancerProfile.onboarding.currentStatus >
				FreelancerOnboardingStatus.HardSkillsEvaluation,
			active:
				freelancerProfile.onboarding.currentStatus ===
				FreelancerOnboardingStatus.HardSkillsEvaluation
		},
		{
			label: t("freelancerApplication.steps.positioningDefinition"),
			description: (
				<Link
					to={
						userSettings?.preferredLanguage === LanguageCode.Fr
							? "https://calendar.app.google/9CYQFfbAa1WmmVNR7"
							: "https://calendar.app.google/KRtg9XPW6zuENE2j7"
					}
					style={{
						textDecoration: "none",
						backgroundColor: zoullooColors.secondary.main,
						color: "white",
						padding: "10px",
						borderRadius: "8px",
						top: "9px",
						position: "relative"
					}}
					target="_blank"
				>
					{t("freelancerApplication.steps.positioningDefinitionButton")}
				</Link>
			),
			completed:
				freelancerProfile.onboarding.currentStatus >
				FreelancerOnboardingStatus.Positioning,
			active:
				freelancerProfile.onboarding.currentStatus ===
				FreelancerOnboardingStatus.Positioning
		},
		{
			label: t("freelancerApplication.steps.communityJoin"),
			description: "",
			completed:
				freelancerProfile.onboarding.currentStatus ===
				FreelancerOnboardingStatus.Accepted,
			active: false
		}
	];

	return (
		<Box sx={{ display: "flex", justifyContent: "flex-start", p: 3 }}>
			<Card sx={{ width: "100%" }} variant="outlined">
				<CardContent sx={{ p: 4 }}>
					{/* Header Section */}
					{freelancerProfile.onboarding.currentStatus ===
					FreelancerOnboardingStatus.Accepted ? (
						<FreelancerOnboardingExperienceRating
							freelancerOnboarding={freelancerProfile.onboarding}
						/>
					) : (
						<FreelancerOnboardingApplicationMessage />
					)}

					{/* Process Stepper */}
					<Box sx={{ mt: 4 }}>
						<Stepper orientation="vertical" nonLinear>
							{steps.map((step, index) => {
								const stepKey = `step-${index}`;
								const fontWeight = step.completed || step.active ? "medium" : "normal";
								const textColor =
									step.completed || step.active ? "text.primary" : "text.secondary";

								return (
									<Step key={stepKey} completed={step.completed} active={step.active}>
										<StepLabel
											icon={
												<CustomStepIcon
													active={step.active}
													completed={step.completed}
													index={index}
												/>
											}
										>
											<Typography
												variant="h6"
												sx={{
													fontWeight,
													color: textColor
												}}
											>
												{step.label}
											</Typography>
										</StepLabel>
										{step.description && (
											<StepContent>
												<Typography variant="body2" color="text.secondary">
													{step.description}
												</Typography>
											</StepContent>
										)}
									</Step>
								);
							})}
						</Stepper>
					</Box>
				</CardContent>
			</Card>
		</Box>
	);
};

export default FreelancerApplication;
